/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React from "react";
import { ScrollView, TouchableOpacity, View, FlatList } from "react-native";
import Rodal from "rodal";
import "rodal/lib/rodal.css";
import parse from "html-react-parser";
import Icon from "react-icons-kit";
import { ic_close } from "react-icons-kit/md/ic_close";
import { check } from "react-icons-kit/fa/check";

import { useSelector } from "react-redux";
import { RootState } from "store/types";

import {
  formattedDateAndTimeString,
  processSkillString,
} from "config/Utilities";
import { FadeInOutView, ProfileImage, Text } from "UpduoLibrary";
import {
  skaleLightGray,
  skalePrimaryBlue,
  skaleWhite,
  skaleCircleGray,
  skaleDarkGray,
  skaleBlack,
  skaleTextSecondaryGray,
  colorWithOpacity,
  skaleWebTextGray,
  skaleTextBlue,
  skaleWebTextLightGray,
  skaleGreenText,
  skaleWebBackgroundGray,
} from "config/UpduoColors";

interface SkillProps {
  id: number;
  name: string;
  is_active: boolean;
  is_published: boolean;
}
interface SkillModalProps {
  isVisible: boolean;
  onDismiss: Function;
  skill: { id: string; name: string; description: string };
  summitSkillList: Array<SkillProps>;
  userId: string;
  communityName: string;
  userSummitSessionList: Array<any>;
}

export default function SkillModal({
  isVisible,
  onDismiss,
  skill,
  summitSkillList,
  userId,
  userSummitSessionList,
}: SkillModalProps) {
  const partnerObject = useSelector((state: RootState) => state.partnerObject);
  const selfObject = useSelector((state: RootState) => state.selfObject);

  const skillDescriptionCleaned = skill.description
    ? processSkillString(
        skill.description,
        undefined,
        undefined,
        undefined,
        undefined,
        skill.name,
        undefined,
        selfObject.organizationName
      )
    : "";
  const partnerName = `${partnerObject.firstName} ${partnerObject.lastName}`;

  const _handleSummitSkillListRenderItem = ({ item }) => {
    const skillId = item.id;
    const filteredUserSummitSessionList = userSummitSessionList.filter(
      (item) => item.skill.id === skillId
    );
    const hasCompletedSkill = filteredUserSummitSessionList.length > 0;
    let skillTextColor = skaleBlack;
    let skillBulletPointColor = skalePrimaryBlue;
    let skillBulletPointCheckColor = skalePrimaryBlue;
    if (item.isPublished) {
      if (skillId === skill.id) {
        skillTextColor = skaleGreenText;
        skillBulletPointColor = skaleGreenText;
        skillBulletPointCheckColor = hasCompletedSkill
          ? skaleWhite
          : skaleGreenText;
      } else if (hasCompletedSkill) {
        skillTextColor = skalePrimaryBlue;
        skillBulletPointColor = skalePrimaryBlue;
        skillBulletPointCheckColor = skaleWhite;
      } else {
        skillTextColor = skaleWebTextGray;
        skillBulletPointColor = skaleDarkGray;
        skillBulletPointCheckColor = skaleDarkGray;
      }
    } else {
      skillTextColor = skaleLightGray;
      skillBulletPointColor = skaleLightGray;
      skillBulletPointCheckColor = skaleLightGray;
    }

    const conversationTitleContent = (
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 4,
          paddingRight: 10,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            maxWidth: 340,
            paddingRight: 10,
          }}
        >
          <View
            style={{
              marginRight: 8,
              // bullet point color
              backgroundColor: skillBulletPointColor,
              width: 18,
              height: 18,
              borderRadius: 9,
            }}
          >
            <Text
              style={{
                // skill check icon color
                color: skillBulletPointCheckColor,
                marginTop: -1,
                textAlign: "center",
              }}
            >
              <Icon size={11} icon={check} />
            </Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text
              style={{
                fontFamily: "ObjectSans-Medium",
                fontWeight: "bold",
                fontSize: 16,
                // skill text color
                color: skillTextColor,
              }}
            >
              {item.name}
            </Text>
          </View>
        </View>
        <Text
          style={{
            fontSize: 14,
            color:
              item.id === skill.id
                ? skaleGreenText
                : hasCompletedSkill
                ? skalePrimaryBlue
                : skaleCircleGray,
          }}
        >
          {item.id === skill.id
            ? "in progress"
            : hasCompletedSkill
            ? "completed"
            : `about ${item.suggestedTotalMinutes} minutes`}
        </Text>
      </View>
    );
    const conversationContent = (
      <View style={{ flex: 1 }}>
        {conversationTitleContent}
        <View
          style={{
            marginTop: 5,
            marginLeft: 26,
            flex: 1,
          }}
        >
          <FlatList
            getItemLayout={(data, index) => ({
              length: 0,
              offset: 0 * index,
              index,
            })}
            ListFooterComponent={
              hasCompletedSkill ? (
                <View style={{ height: 8 }} />
              ) : item.id === skill.id ? (
                <View style={{ height: 0 }} />
              ) : null
            }
            keyExtractor={(_, index) => String(index)}
            data={filteredUserSummitSessionList}
            renderItem={({ item }) => {
              if (item.skill.id === skill.id && !hasCompletedSkill) {
                return <View style={{ height: 0 }} />;
              }
              const historySessionTextColor = item.skill.is_published
                ? skaleWebTextLightGray
                : skaleLightGray;
              return (
                <View
                  style={{
                    flexDirection: "row",
                    flex: 1,
                    paddingVertical: 1,
                  }}
                >
                  <Text
                    style={{
                      fontFamily: "ObjectSans-Medium",
                      color: historySessionTextColor,
                      fontSize: 16,
                    }}
                  >
                    {`${formattedDateAndTimeString(
                      new Date(Date.parse(item.created))
                    )} with `}
                  </Text>
                  <Text
                    style={{
                      fontFamily: "ObjectSans-Medium",
                      fontWeight: "bold",
                      fontSize: 16,
                      color: historySessionTextColor,
                    }}
                  >
                    {item.learner.id !== userId
                      ? `${item.learner.firstName} ${item.learner.lastName}`
                      : `${item.teacher.firstName} ${item.teacher.lastName}`}
                  </Text>
                </View>
              );
            }}
          />
        </View>
      </View>
    );

    return (
      <View
        style={{
          flexDirection: "row",
          justifyContent: "flex-start",
        }}
        key={item.id}
      >
        {conversationContent}
      </View>
    );
  };

  return (
    <Rodal
      customStyles={{
        backgroundColor: "transparent",
        boxShadow: "0 0px 0px rgba(0, 0, 0, 0)",
        bottom: "50%",
      }}
      visible={isVisible}
      onClose={() => onDismiss()}
    >
      <View
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <FadeInOutView
          style={{
            paddingTop: 30,
            paddingBottom: 40,
            paddingHorizontal: 30,
            alignItems: "center",
            backgroundColor: skaleWhite,
            borderRadius: 18,
          }}
        >
          <Text
            isHeading={true}
            style={{
              fontSize: 16,
              textAlign: "center",
              color: skaleBlack,
            }}
          >
            {"Topic"}
          </Text>
          <View
            style={{
              maxHeight: 50,
              marginBottom: 10,
            }}
          >
            <Text
              isHeading={true}
              style={{
                fontSize: 18,
                textAlign: "center",
                color: skalePrimaryBlue,
              }}
            >
              {skill.name}
            </Text>
          </View>
          <ScrollView
            style={{
              flexGrow: 0,
              maxHeight: "60vh",
              width: 450,
              paddingRight: 5,
            }}
          >
            <Text
              style={{
                fontSize: 16,
                color: skaleBlack,
              }}
            >
              {parse(skillDescriptionCleaned)}
            </Text>
            <FlatList
              getItemLayout={(data, index) => ({
                length: 0,
                offset: 0 * index,
                index,
              })}
              scrollEnabled={false}
              keyExtractor={(_, index) => String(index)}
              data={summitSkillList}
              renderItem={_handleSummitSkillListRenderItem}
              ItemSeparatorComponent={() => (
                <View
                  style={{
                    marginVertical: 14,
                    borderTopWidth: 1,
                    borderColor: skaleWebBackgroundGray,
                  }}
                />
              )}
            />
            <View style={{ width: "100%", marginTop: 10 }}>
              <Text
                style={{
                  color: colorWithOpacity(skaleTextSecondaryGray, 0.4),
                  fontSize: 14,
                }}
              >
                {"PARTNER"}
              </Text>
              <View
                style={{
                  paddingVertical: 24,
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <ProfileImage size={40} userObject={partnerObject} />
                <Text isHeading={true} style={{ paddingHorizontal: 13 }}>
                  {partnerName}
                </Text>
              </View>
            </View>
          </ScrollView>
          <View style={{ position: "absolute", top: 0, right: 0 }}>
            <TouchableOpacity
              onPress={() => {
                onDismiss();
              }}
              style={{
                paddingTop: 16,
                paddingRight: 16,
                alignSelf: "flex-end",
              }}
            >
              <div
                style={{
                  color: skaleTextBlue,
                  paddingBottom: 2.5,
                }}
              >
                <Icon size={25} icon={ic_close} />
              </div>
            </TouchableOpacity>
          </View>
        </FadeInOutView>
      </View>
    </Rodal>
  );
}
